import type { UserType } from "@shared/types";
import { apiClient } from "@/lib/api-client";
import {
	clearTokens,
	getOrCreateDeviceId,
	setDeviceId,
	setTokens,
} from "@/lib/token-manager";

export interface AdminBootstrap {
	operator: {
		id: string;
		phone: string;
		userType: UserType | null;
	};
	authorization: {
		isAdmin: true;
		// Forward-compatible. v1 returns ["admin:*"]; future RBAC will return
		// scoped permissions like "admin:payments:release".
		permissions: string[];
	};
}

export async function sendOtp(phone: string) {
	const cleanPhone = phone.replace(/\D/g, "").slice(-10);
	// Admin-scoped OTP endpoint. Returns the same generic shape regardless of
	// whether the phone is a known admin — prevents phone enumeration.
	const response = await apiClient.post("/api/v1/admin/auth/send-otp", {
		phone: cleanPhone,
	});
	return response.data;
}

export async function verifyOtp(phone: string, otp: string) {
	const cleanPhone = phone.replace(/\D/g, "").slice(-10);
	// The verify page shows a refused code in its own banner.
	const response = await apiClient.post(
		"/api/v1/admin/auth/verify-otp",
		{
			phone: cleanPhone,
			otp,
			// Stable per-install id so the backend can label this device session.
			deviceId: getOrCreateDeviceId(),
		},
		{ inlineErrors: true },
	);

	// Access token + session live in HttpOnly cookies set by the backend; just
	// record that this device now has a session.
	if (response.data?.success) {
		setTokens();
		// Keep the id the backend actually stored, not the one we generated, so
		// the next admin login from this browser reuses the same device id.
		if (response.data.data?.deviceId) {
			setDeviceId(response.data.data.deviceId);
		}
	}
	return response.data;
}

export async function getAdminMe(): Promise<AdminBootstrap> {
	const response = await apiClient.get("/api/v1/admin/me");
	return response.data.data;
}

export async function logout(): Promise<void> {
	try {
		// The backend revokes the current admin session via the admin session
		// cookie and clears only the admin cookies; no body.
		await apiClient.post("/api/v1/admin/auth/logout");
	} finally {
		clearTokens();
	}
}
